// @src/pre-deployment/src/components/GrammarFeature.js v1.2
// - Updated for ChapterContainer compatibility
// - Uses props from ChapterContainer instead of useParams
// - Fixed navigation handling for new App.js structure

import React, { useState, useEffect } from 'react';

import { useAuth } from '../../contexts/AuthContext';
import { useGrammarPoints, useComprehensiveGrammar, useSessionProgressActions } from '../../data/useDataService';
import './GrammarFeature.css';

const GrammarFeature = ({ _moduleKey, _moduleConfig, navigation, _modulesOrder, _moduleMap, level, chapter }) => {
    const { user } = useAuth();

    const sessionType = 'Learn';
    const moduleType = 'grammar';

    // State for grammar learning
    const [currentGrammarIndex, setCurrentGrammarIndex] = useState(0);
    const [selectedExample, setSelectedExample] = useState(null);
    const [completedGrammar, setCompletedGrammar] = useState(new Set());
    const [exerciseNumber, setExerciseNumber] = useState(1);

    // Sound effects helper
    const playSound = (type) => {
        const sounds = {
            click: '/sounds/click.mp3',
            correct: '/sounds/correct.mp3',
            incorrect: '/sounds/incorrect.mp3',
            complete: '/sounds/complete.mp3'
        };

        try {
            const audio = new Audio(sounds[type]);
            audio.volume = 0.5;
            audio.play().catch(_e => {
                // Silent fail if file doesn't exist
            });
        } catch (_e) {
            console.error('Error initializing audio:', _e);
        }
    };

    // Data fetching with fallback - app won't break if data fails
    const {
        data: fetchedGrammarData,
        loading: grammarLoading,
        error: grammarError
    } = useGrammarPoints(level, chapter, 'Korea_University');

    const {
        data: comprehensiveGrammar
    } = useComprehensiveGrammar(level, 'Korea_University');

    // Fallback data if Firebase fails
    const fallbackGrammarData = {
        learning: [
            {
                id: 'gmr_001',
                grammarPoint: '~고 있어요 (Progressive tense)',
                explanation: 'Used to express ongoing actions in medical contexts',
                examples: [
                    '환자가 아프고 있어요. (The patient is sick.)',
                    '의사가 진찰하고 있어요. (The doctor is examining.)'
                ]
            }
        ],
        exercises: [],
        total: 1
    };

    // Use fetched data if available, otherwise use fallback
    const grammarData = (fetchedGrammarData && fetchedGrammarData.learning && fetchedGrammarData.learning.length > 0)
        ? fetchedGrammarData
        : fallbackGrammarData;

    // Additional safety check for grammarData structure
    if (grammarData && typeof grammarData !== 'object') {
        console.error('Invalid grammarData structure:', grammarData);
    }

    const {
        saveExerciseAttemptWithSession
    } = useSessionProgressActions(user?.uid, parseInt(level), parseInt(chapter), sessionType);

    // Data validation function to handle different data structures
    const validateGrammarData = (grammarData) => {
        if (!grammarData || typeof grammarData !== 'object') return null;

        // Ensure all values are safe for React rendering
        const safeString = (value) => {
            if (value === null || value === undefined) return '';
            if (typeof value === 'string') return value;
            if (typeof value === 'number') return String(value);
            if (typeof value === 'boolean') return String(value);
            if (typeof value === 'object') return JSON.stringify(value);
            return String(value);
        };

        const safeArray = (value) => {
            if (!Array.isArray(value)) return [];
            return value.map(item => {
                if (typeof item === 'string') return item;
                if (typeof item === 'object' && item !== null) {
                    return {
                        ...item,
                        // Ensure all object properties are strings
                        sentence_kr: safeString(item.sentence_kr),
                        text: safeString(item.text),
                        description: safeString(item.description),
                        rule: safeString(item.rule),
                        name: safeString(item.name),
                        form: safeString(item.form)
                    };
                }
                return safeString(item);
            });
        };

        return {
            // Handle different possible title fields - ensure strings
            titleKR: safeString(grammarData.title_kr || grammarData.titleKR || grammarData.grammarPoint || 'Grammar Point'),
            titleEN: safeString(grammarData.title_en || grammarData.titleEN || grammarData.usage || 'Usage'),

            // Handle different possible explanation fields - ensure strings
            explanation: safeString(grammarData.explanation || grammarData.brief_notes || grammarData.usage || 'Explanation not available'),

            // Handle different possible example fields - ensure arrays
            examples: safeArray(grammarData.regular_examples || grammarData.examples || grammarData.step1?.examples || []),
            conversationExamples: safeArray(grammarData.conversation_examples || grammarData.conversationExamples || []),
            irregularExamples: safeArray(grammarData.irregular_examples || grammarData.irregularExamples || []),

            // Handle forms and rules - ensure arrays
            forms: safeArray(grammarData.forms || []),
            rules: safeArray(grammarData.rules || []),

            // Handle other fields - ensure strings
            nuance: safeString(grammarData.nuance || ''),
            register: safeString(grammarData.register || 'beginner'),
            grammar_group: safeString(grammarData.grammar_group || grammarData.category || 'Category'),
            chapter: safeString(grammarData.chapter || 'Chapter'),

            // Keep original data for debugging
            originalData: grammarData
        };
    };

    // Get current grammar point - moved before useEffect to avoid initialization error
    let currentGrammar = null;
    let _chapterInfo = null;

    try {
        // Handle different possible data structures
        if (grammarData) {
            console.log('Grammar data structure:', {
                hasLearning: !!grammarData.learning,
                learningType: typeof grammarData.learning,
                isArray: Array.isArray(grammarData.learning),
                learningLength: grammarData.learning?.length,
                keys: Object.keys(grammarData)
            });

            // Try different possible structures
            let rawGrammar = null;
            if (grammarData.learning && Array.isArray(grammarData.learning)) {
                rawGrammar = grammarData.learning[currentGrammarIndex];
            } else if (Array.isArray(grammarData)) {
                // If grammarData is directly an array
                rawGrammar = grammarData[currentGrammarIndex];
            } else if (grammarData.data && Array.isArray(grammarData.data)) {
                // If data is nested under 'data' property
                rawGrammar = grammarData.data[currentGrammarIndex];
            } else {
                console.error('Unexpected grammar data structure:', grammarData);
                rawGrammar = null;
            }

            // Validate and normalize the grammar data
            currentGrammar = validateGrammarData(rawGrammar);
        }

        _chapterInfo = comprehensiveGrammar?.chapters?.[chapter];

    } catch (error) {
        console.error('❌ Error processing grammar data:', error);
        currentGrammar = null;
        _chapterInfo = null;
    }

    // Initialize component
    useEffect(() => {
        console.group('📚 GRAMMAR FEATURE: Component loaded');
        console.log('Level:', level, 'Chapter:', chapter);
        console.log('Grammar data:', grammarData);
        console.log('Current grammar:', currentGrammar);
        if (currentGrammar) {
            console.log('Current grammar type:', typeof currentGrammar);
            console.log('Current grammar keys:', Object.keys(currentGrammar));

            // Debug the actual data structure
            console.log('=== DATA STRUCTURE ANALYSIS ===');
            console.log('title_kr:', currentGrammar.title_kr);
            console.log('title_en:', currentGrammar.title_en);
            console.log('titleKR:', currentGrammar.titleKR);
            console.log('titleEN:', currentGrammar.titleEN);
            console.log('explanation:', currentGrammar.explanation);
            console.log('regular_examples:', currentGrammar.regular_examples);
            console.log('conversation_examples:', currentGrammar.conversation_examples);
            console.log('examples:', currentGrammar.examples);
            console.log('step1:', currentGrammar.step1);
            console.log('forms:', currentGrammar.forms);
            console.log('rules:', currentGrammar.rules);
        }
        console.groupEnd();
    }, [level, chapter, grammarData, currentGrammar]);

    // Handle grammar completion
    const handleGrammarComplete = async (grammarId) => {
        playSound('complete');
        const newCompleted = new Set(completedGrammar);
        newCompleted.add(grammarId);
        setCompletedGrammar(newCompleted);

        // Save progress with session tracking - with error handling
        if (user?.uid) {
            try {
                await saveExerciseAttemptWithSession({
                    exercise_id: `grammar_${level}_${chapter}_${grammarId}`,
                    user_answer: 'completed',
                    correct_answer: 'completed',
                    is_correct: true,
                    first_attempt: true,
                    response_time_sec: 0,
                    attempt_time: new Date().toISOString(),
                    difficulty: 'beginner',
                    category: 'Grammar Practice',
                    subcategory: 'grammar_completion',
                    module_type: moduleType,
                    exercise_number: exerciseNumber
                });

                console.log('✅ Grammar progress saved successfully');
                console.log('Data path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/{moduleType}');
                console.log('Progress path: users/{userId}/level_{level}/chapter_{chapter}/{sessionType}/LearningProgress');

                setExerciseNumber(prev => prev + 1);
            } catch (error) {
                console.error('❌ Failed to save grammar progress:', error);
                console.log('⚠️ Continuing without saving progress due to Firebase error');
                // Don't throw the error - just log it and continue
            }
        }

        console.log(`✅ Grammar point ${grammarId} completed`);
    };

    const handleNextFeature = () => {
        playSound('click');
        navigation?.next();
    };

    // Grammar navigation
    const handleNextGrammar = () => {
        playSound('click');
        if (grammarData?.learning && currentGrammarIndex < grammarData.learning.length - 1) {
            setCurrentGrammarIndex(currentGrammarIndex + 1);
            setSelectedExample(null);
        }
    };

    const handlePreviousGrammar = () => {
        playSound('click');
        if (currentGrammarIndex > 0) {
            setCurrentGrammarIndex(currentGrammarIndex - 1);
            setSelectedExample(null);
        }
    };


    // Loading state
    if (grammarLoading) {
        return (
            <div className="grammar-feature-container">
                <div className="grammar-content">
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #FFA500',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 1rem'
                    }}></div>
                    <p>Loading grammar content...</p>
                </div>
            </div>
        );
    }

    // Error state - but still allow navigation
    if (grammarError) {
        return (
            <div className="grammar-feature-container">
                <div className="grammar-content">
                    <h3 style={{ color: '#b30000' }}>❌ Error Loading Grammar</h3>
                    <p>{grammarError.message}</p>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
                        This might be due to network issues or ad blockers. You can still continue to the next module.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Retry
                        </button>
                        <button
                            onClick={handleNextFeature}
                            style={{
                                backgroundColor: '#FFA500',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Continue to Next Module
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // No data state - but still allow navigation
    if (!grammarData || !grammarData.learning || grammarData.learning.length === 0) {
        return (
            <div className="grammar-feature-container">
                <div className="grammar-content">
                    <h3>📚 No Grammar Content Available</h3>
                    <p>Grammar content for Level {level}, Chapter {chapter} is not available yet.</p>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
                        You can still continue to the next module.
                    </p>
                    <button
                        onClick={handleNextFeature}
                        style={{
                            marginTop: '1rem',
                            backgroundColor: '#FFA500',
                            color: 'white',
                            border: 'none',
                            padding: '1rem 2rem',
                            borderRadius: '6px',
                            cursor: 'pointer'
                        }}
                    >
                        Continue to Next Module
                    </button>
                </div>
            </div>
        );
    }

    // Safety check for invalid learning array - but still allow navigation
    if (!Array.isArray(grammarData.learning)) {
        return (
            <div className="grammar-feature-container">
                <div className="grammar-content">
                    <h3 style={{ color: '#b30000' }}>⚠️ Invalid Grammar Data</h3>
                    <p>The grammar data structure is invalid. Please try refreshing the page.</p>
                    <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
                        You can still continue to the next module.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Refresh Page
                        </button>
                        <button
                            onClick={handleNextFeature}
                            style={{
                                backgroundColor: '#FFA500',
                                color: 'white',
                                border: 'none',
                                padding: '1rem 2rem',
                                borderRadius: '6px',
                                cursor: 'pointer'
                            }}
                        >
                            Continue to Next Module
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="grammar-feature-container">
            <div className="grammar-content">
                {currentGrammar && typeof currentGrammar === 'object' ? (
                    <div className="grammar-lesson">
                        {/* Grammar Point Header */}
                        <div className="grammar-point-header">
                            <div className="grammar-point-title">
                                <h3>{currentGrammar.titleKR}</h3>
                                <span className={`difficulty-badge ${currentGrammar.register}`}>
                                    {currentGrammar.register}
                                </span>
                            </div>
                            <div className="grammar-point-meta">
                                <span className="category">{currentGrammar.grammar_group}</span>
                                <span className="chapter">{currentGrammar.chapter}</span>
                            </div>
                        </div>

                        {/* Usage and Explanation */}
                        <div className="grammar-explanation">
                            <div className="usage-section">
                                <h4>📖 Usage</h4>
                                <p>{currentGrammar.explanation}</p>
                            </div>

                            <div className="explanation-section">
                                <h4>💡 Explanation</h4>
                                <p>{currentGrammar.explanation}</p>
                            </div>

                            {currentGrammar.nuance && currentGrammar.nuance.trim() !== '' && (
                                <div className="nuance-section">
                                    <h4>🎯 Nuance</h4>
                                    <p>{currentGrammar.nuance}</p>
                                </div>
                            )}
                        </div>

                        {/* Grammar Rules */}
                        {currentGrammar.rules && Array.isArray(currentGrammar.rules) && currentGrammar.rules.length > 0 && (
                            <div className="grammar-rules">
                                <h4>📋 Grammar Rules</h4>
                                <ul>
                                    {currentGrammar.rules.map((rule, index) => (
                                        <li key={index}>
                                            {typeof rule === 'string' ? rule :
                                                typeof rule === 'object' && rule.description ? rule.description :
                                                    typeof rule === 'object' && rule.rule ? rule.rule :
                                                        'Rule description'}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Grammar Forms */}
                        {currentGrammar.forms && Array.isArray(currentGrammar.forms) && currentGrammar.forms.length > 0 && (
                            <div className="grammar-forms">
                                <h4>🔤 Grammar Forms</h4>
                                <div className="forms-grid">
                                    {currentGrammar.forms.map((form, index) => (
                                        <div key={index} className="form-card">
                                            {typeof form === 'string' ? form :
                                                typeof form === 'object' && form.name ? form.name :
                                                    typeof form === 'object' && form.form ? form.form :
                                                        'Form description'}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Examples Section */}
                        <div className="examples-section">
                            <h4>📝 Examples</h4>
                            <div className="examples-grid">
                                {/* Regular Examples */}
                                {currentGrammar.examples && Array.isArray(currentGrammar.examples) && currentGrammar.examples.length > 0 && currentGrammar.examples.map((example, index) => (
                                    <div
                                        key={`regular-${index}`}
                                        className={`example-card ${selectedExample === `regular-${index}` ? 'selected' : ''}`}
                                        onClick={() => {
                                            playSound('click');
                                            setSelectedExample(selectedExample === `regular-${index}` ? null : `regular-${index}`);
                                        }}
                                    >
                                        <div className="example-text">
                                            {typeof example === 'string' ? example :
                                                typeof example === 'object' && example.sentence_kr ? example.sentence_kr :
                                                    typeof example === 'object' && example.text ? example.text :
                                                        'Example text'}
                                        </div>
                                        {selectedExample === `regular-${index}` && (
                                            <div className="example-breakdown">
                                                <p>Click to analyze this example</p>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Conversation Examples */}
                                {currentGrammar.conversationExamples && Array.isArray(currentGrammar.conversationExamples) && currentGrammar.conversationExamples.length > 0 && currentGrammar.conversationExamples.map((example, index) => (
                                    <div
                                        key={`conversation-${index}`}
                                        className={`example-card conversation ${selectedExample === `conversation-${index}` ? 'selected' : ''}`}
                                        onClick={() => {
                                            playSound('click');
                                            setSelectedExample(selectedExample === `conversation-${index}` ? null : `conversation-${index}`);
                                        }}
                                    >
                                        <div className="example-text">
                                            {typeof example === 'string' ? example :
                                                typeof example === 'object' && example.sentence_kr ? example.sentence_kr :
                                                    typeof example === 'object' && example.text ? example.text :
                                                        'Example text'}
                                        </div>
                                        {selectedExample === `conversation-${index}` && (
                                            <div className="example-breakdown">
                                                <p>Click to analyze this example</p>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {/* Irregular Examples */}
                                {currentGrammar.irregularExamples && Array.isArray(currentGrammar.irregularExamples) && currentGrammar.irregularExamples.length > 0 && currentGrammar.irregularExamples.map((example, index) => (
                                    <div
                                        key={`irregular-${index}`}
                                        className={`example-card irregular ${selectedExample === `irregular-${index}` ? 'selected' : ''}`}
                                        onClick={() => {
                                            playSound('click');
                                            setSelectedExample(selectedExample === `irregular-${index}` ? null : `irregular-${index}`);
                                        }}
                                    >
                                        <div className="example-text">
                                            {typeof example === 'string' ? example :
                                                typeof example === 'object' && example.sentence_kr ? example.sentence_kr :
                                                    typeof example === 'object' && example.text ? example.text :
                                                        'Example text'}
                                        </div>
                                        {selectedExample === `irregular-${index}` && (
                                            <div className="example-breakdown">
                                                <p>Click to analyze this example</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Grammar Navigation */}
                        <div className="grammar-navigation">
                            <button
                                className="nav-btn prev-btn"
                                onClick={handlePreviousGrammar}
                                disabled={currentGrammarIndex === 0}
                            >
                                <i className="fas fa-chevron-left"></i>
                                Previous
                            </button>

                            <div className="grammar-progress">
                                {grammarData.learning && Array.isArray(grammarData.learning) && grammarData.learning.map((grammar, index) => (
                                    <div
                                        key={index}
                                        className={`progress-dot ${index === currentGrammarIndex ? 'active' : ''} ${completedGrammar.has(grammar.id || `grammar_${index}`) ? 'completed' : ''}`}
                                        onClick={() => {
                                            playSound('click');
                                            setCurrentGrammarIndex(index);
                                        }}
                                    />
                                ))}
                            </div>

                            <button
                                className="nav-btn next-btn"
                                onClick={handleNextGrammar}
                                disabled={currentGrammarIndex === (grammarData.learning ? grammarData.learning.length - 1 : 0)}
                            >
                                Next
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>

                        {/* Complete Button */}
                        <div className="completion-section">
                            <button
                                className="complete-btn"
                                onClick={() => handleGrammarComplete(currentGrammar.originalData?.id || 'grammar_point')}
                                disabled={completedGrammar.has(currentGrammar.originalData?.id || 'grammar_point')}
                            >
                                {completedGrammar.has(currentGrammar.originalData?.id || 'grammar_point') ? (
                                    <>
                                        <i className="fas fa-check"></i>
                                        Completed
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-check-circle"></i>
                                        Mark as Complete
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="error-container">
                        <h3>⚠️ Invalid Grammar Data</h3>
                        <p>The grammar data structure is invalid. Please try refreshing the page.</p>
                        <p style={{ color: '#666', fontSize: '0.9rem', marginTop: '1rem' }}>
                            You can still continue to the next module.
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                            <button onClick={() => window.location.reload()}>Refresh Page</button>
                            <button
                                className="start-button"
                                onClick={handleNextFeature}
                            >
                                Continue to Next Module
                            </button>
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default GrammarFeature;
